import * as yup from 'yup';

import { STRINGS } from '@/constants/strings';
import { emailRule } from '@/validation';

const MESSAGES = STRINGS.guardian.reportIssue;
const SHARED_MESSAGES = STRINGS.mutatePlayer.validation;

/** Short issue title (BR-02); the input blocks typing past this length. */
export const MAX_REPORT_ISSUE_LENGTH = 100;

/** Optional extra detail (BR-02). Input uses maxLength; yup still validates on submit. */
export const MAX_REPORT_DESCRIPTION_LENGTH = 500;

/** Max image attachments per report (Evidence field). */
export const MAX_REPORT_IMAGES = 5;

/** Max video attachments per report (Evidence field). */
export const MAX_REPORT_VIDEOS = 1;

/** Combined cap: up to 5 images + 1 video. */
export const MAX_REPORT_ATTACHMENTS = MAX_REPORT_IMAGES + MAX_REPORT_VIDEOS;

/** Per-image upload cap for bug-report evidence. */
export const MAX_REPORT_IMAGE_FILE_SIZE = 10 * 1024 * 1024;

/** Per-video upload cap for bug-report evidence. */
export const MAX_REPORT_VIDEO_FILE_SIZE = 50 * 1024 * 1024;

/** A picked image or video, held locally until the report is submitted. */
export type ReportAttachment = {
  /** Local `file://` path of the picked media. */
  uri: string;
  /** Display name shown in the picker row / preview tile. */
  name: string;
  /** MIME type, used for the storage upload's content type. */
  mime: string;
  kind: 'image' | 'video';
  fileSize?: number;
};

/** Remaining image / video slots given what's already attached. */
export function getReportAttachmentCapacity(current: ReportAttachment[]) {
  const imageCount = current.filter(item => item.kind === 'image').length;
  const videoCount = current.filter(item => item.kind === 'video').length;
  const withinLimits = imageCount <= MAX_REPORT_IMAGES && videoCount <= MAX_REPORT_VIDEOS;
  const remainingImages = Math.max(0, MAX_REPORT_IMAGES - imageCount);
  const remainingVideos = Math.max(0, MAX_REPORT_VIDEOS - videoCount);
  const remaining = withinLimits ? remainingImages + remainingVideos : 0;
  return {
    imageCount,
    videoCount,
    remainingImages,
    remainingVideos,
    remaining,
    withinLimits,
    // Block further picks while over-limit so the guardian must remove extras first.
    canAddMore: withinLimits && (remainingImages > 0 || remainingVideos > 0),
  };
}

/**
 * System-picker constraints so the library can't offer a 6th image or a 2nd video (native).
 * On web the OS dialog can't enforce counts — extras are kept in UI and fail validation instead.
 * - Images full → video-only
 * - Video full → photo-only
 * - Both open → mixed, capped at remaining image slots (≤ 5) on native
 */
export function getReportAttachmentPickerOptions(current: ReportAttachment[]): {
  maxFiles: number;
  mediaType: 'any' | 'photo' | 'video';
} {
  const { remainingImages, remainingVideos, canAddMore } = getReportAttachmentCapacity(current);
  if (!canAddMore) return { maxFiles: 0, mediaType: 'any' };
  if (remainingImages <= 0) return { maxFiles: remainingVideos, mediaType: 'video' };
  if (remainingVideos <= 0) return { maxFiles: remainingImages, mediaType: 'photo' };
  return { maxFiles: remainingImages, mediaType: 'any' };
}

export function isReportAttachmentWithinSizeLimit(attachment: ReportAttachment) {
  if (attachment.fileSize == null) return true;
  const max = attachment.kind === 'video' ? MAX_REPORT_VIDEO_FILE_SIZE : MAX_REPORT_IMAGE_FILE_SIZE;
  return attachment.fileSize <= max;
}

export type ReportAttachmentValidationIssue =
  | 'oversize-image'
  | 'oversize-video'
  | 'oversize-both'
  | 'limit'
  | null;

/**
 * Inline Evidence validation. Size issues follow SC7–SC9 (image-only / video-only / both);
 * size wins over count so the guardian sees the size copy first.
 */
export function getReportAttachmentValidationIssue(
  attachments: ReportAttachment[],
): ReportAttachmentValidationIssue {
  const oversizeImage = attachments.some(
    item => item.kind === 'image' && !isReportAttachmentWithinSizeLimit(item),
  );
  const oversizeVideo = attachments.some(
    item => item.kind === 'video' && !isReportAttachmentWithinSizeLimit(item),
  );
  if (oversizeImage && oversizeVideo) return 'oversize-both';
  if (oversizeImage) return 'oversize-image';
  if (oversizeVideo) return 'oversize-video';

  const imageCount = attachments.filter(item => item.kind === 'image').length;
  const videoCount = attachments.filter(item => item.kind === 'video').length;
  if (imageCount > MAX_REPORT_IMAGES || videoCount > MAX_REPORT_VIDEOS) return 'limit';
  return null;
}

/** Append newly picked media without dropping over-limit / oversize items (validation UX). */
export function mergeReportAttachments(
  current: ReportAttachment[],
  picked: ReportAttachment[],
): ReportAttachment[] {
  const merged = [...current];
  for (const item of picked) {
    if (merged.some(existing => existing.uri === item.uri)) continue;
    merged.push(item);
  }
  return merged;
}

/**
 * "Report an issue" form. Name and email are prefilled from the guardian account and shown
 * read-only (BR-01), so `issue` is the only field the guardian must fill; `description` is
 * optional additional detail (BR-02).
 */
export const guardianReportIssueSchema = yup
  .object({
    firstName: yup.string().trim().required(MESSAGES.fieldRequired),
    lastName: yup.string().trim().required(MESSAGES.fieldRequired),
    email: emailRule(MESSAGES.fieldRequired, SHARED_MESSAGES.emailInvalid),
    issue: yup.string().trim().required(MESSAGES.fieldRequired).max(MAX_REPORT_ISSUE_LENGTH),
    description: yup
      .string()
      .trim()
      .defined()
      .max(MAX_REPORT_DESCRIPTION_LENGTH, MESSAGES.descriptionMaxLength),
  })
  .required();

export type GuardianReportIssueForm = {
  firstName: string;
  lastName: string;
  email: string;
  issue: string;
  description: string;
};

export const INITIAL_GUARDIAN_REPORT_ISSUE_FORM: GuardianReportIssueForm = {
  firstName: '',
  lastName: '',
  email: '',
  issue: '',
  description: '',
};
